import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Feather, Clock, User, LogOut, Wind, Droplet, Flame, Heart, Sparkles, Coffee, X, Crown, ChevronRight, ShoppingBag, Bookmark, Anchor, Sun, Eye, Target, Pause, Shield, Music, Star, Zap, BookOpen, Smartphone } from "lucide-react";
import { API } from "../App";
import Footer from "../components/Footer";
import { useInstallPrompt } from "../hooks/useInstallPrompt";

const RITUAL_ICONS = {
  wind: Wind,
  droplet: Droplet,
  flame: Flame,
  heart: Heart,
  sparkles: Sparkles,
  coffee: Coffee,
  anchor: Anchor,
  sun: Sun,
  feather: Feather,
  scroll: Feather,  // Using Feather as fallback for scroll
  eye: Eye,
  target: Target,
  pause: Pause,
  shield: Shield,
  music: Music,
  star: Star,
  zap: Zap
};

export default function Dashboard({ auth }) {
  const navigate = useNavigate();
  const [rituals, setRituals] = useState([]);
  const [recentSessions, setRecentSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRitual, setSelectedRitual] = useState(null);
  const [softReflections, setSoftReflections] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [insights, setInsights] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(true);
  const { isInstallable, promptInstall } = useInstallPrompt();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ritualsRes, sessionsRes, subscriptionRes, insightsRes] = await Promise.all([
        fetch(`${API}/rituals`, { credentials: 'include' }),
        fetch(`${API}/sessions?limit=5`, { credentials: 'include' }),
        fetch(`${API}/subscription/status`, { credentials: 'include' }),
        fetch(`${API}/insights`, { credentials: 'include' })
      ]);

      if (ritualsRes.ok) {
        const ritualsData = await ritualsRes.json();
        setRituals(ritualsData);
      }

      if (sessionsRes.ok) {
        const sessionsData = await sessionsRes.json();
        setRecentSessions(sessionsData);
        // Generate soft reflections from emotional themes
        generateSoftReflections(sessionsData);
      }

      let isPremium = false;
      if (subscriptionRes.ok) {
        const subData = await subscriptionRes.json();
        setSubscriptionStatus(subData);
        isPremium = subData.plan !== 'free';
      }

      if (insightsRes.ok) {
        const insightsData = await insightsRes.json();
        setInsights(insightsData);
      }

      // Fetch weekly summary for Inner Circle users
      if (isPremium) {
        try {
          const summaryRes = await fetch(`${API}/insights/weekly-summary`, { credentials: 'include' });
          if (summaryRes.ok) {
            const summaryData = await summaryRes.json();
            setWeeklySummary(summaryData);
          }
        } catch (e) {
          console.error('Error fetching weekly summary:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSoftReflections = (sessions) => {
    if (!sessions || sessions.length === 0) return;
    
    // Collect all emotional themes
    const themeCounts = {};
    sessions.forEach(session => {
      const themes = session.emotional_themes || [];
      themes.forEach(theme => {
        themeCounts[theme] = (themeCounts[theme] || 0) + 1;
      });
    });
    
    // Generate reflections based on patterns
    const reflections = [];
    
    if (themeCounts['overwhelm'] >= 2) {
      reflections.push("You often notice this before overextending. That awareness is valuable.");
    }
    if (themeCounts['fatigue'] >= 2) {
      reflections.push("Rest has been asking for your attention lately.");
    }
    if (themeCounts['anxiety'] >= 2) {
      reflections.push("This feeling has visited before. You've moved through it.");
    }
    if (themeCounts['uncertainty'] >= 2) {
      reflections.push("Not knowing is showing up. That takes courage to sit with.");
    }
    if (sessions.length >= 3) {
      reflections.push("You've been showing up for yourself. That consistency matters.");
    }
    
    // Limit to 2 reflections
    setSoftReflections(reflections.slice(0, 2));
  };

  const handleLogout = async () => {
    await auth.logout();
    navigate('/');
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-12 h-12 border-2 border-[#A87C6D] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#4A4744] font-light">Preparing your space...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="dashboard">
      {/* Install App Banner */}
      {isInstallable && showInstallBanner && (
        <div className="bg-gradient-to-r from-[#A87C6D] to-[#C4A99B] text-white px-4 py-3">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm font-light">
                Install The Soft Ritual for a better experience
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={promptInstall}
                className="px-4 py-1.5 bg-white text-[#A87C6D] rounded-full text-sm font-medium hover:bg-white/90 transition-colors"
                data-testid="install-banner-btn"
              >
                Install
              </button>
              <button
                onClick={() => setShowInstallBanner(false)}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="px-6 py-4 border-b border-[#E6E2DD]">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-2xl font-light text-[#2D2A26]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            the soft ritual
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/blog">
              <Button variant="ghost" size="icon" className="rounded-full text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#E6E2DD]" data-testid="blog-btn">
                <BookOpen className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/history">
              <Button variant="ghost" size="icon" className="rounded-full text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#E6E2DD]" data-testid="history-btn">
                <Clock className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/store">
              <Button variant="ghost" size="icon" className="rounded-full text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#E6E2DD]" data-testid="store-btn">
                <ShoppingBag className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/profile">
              <Button variant="ghost" size="icon" className="rounded-full text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#E6E2DD]" data-testid="profile-btn">
                <User className="w-5 h-5" />
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleLogout}
              className="rounded-full text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#E6E2DD]"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-6 py-12 md:py-16">
        {/* Greeting */}
        <div className="text-center mb-12 animate-fade-in">
          <p className="text-[#A87C6D] uppercase tracking-[0.3em] text-sm mb-4 font-medium">
            {getGreeting()}
          </p>
          <h1 
            className="text-4xl md:text-5xl font-light text-[#2D2A26] mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {auth.user?.name?.split(' ')[0] || 'Welcome'}
          </h1>
          <p className="text-[#4A4744] font-light text-lg">
            How are you arriving today?
          </p>
        </div>

        {/* Open User Upgrade Banner */}
        {subscriptionStatus && subscriptionStatus.plan === 'free' && (
          <div className="mb-8 animate-fade-in">
            <div className="paper-card rounded-2xl p-5 border border-[#A87C6D]/20 bg-gradient-to-r from-[#A87C6D]/5 to-transparent">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#A87C6D]/10 flex items-center justify-center flex-shrink-0">
                    <Crown className="w-5 h-5 text-[#A87C6D]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#2D2A26]">
                      {subscriptionStatus.check_ins_used} of {subscriptionStatus.check_in_limit} check-ins used this week
                    </p>
                    <p className="text-xs text-[#4A4744] mt-0.5">
                      Join the Inner Circle for unlimited check-ins & deeper insights
                    </p>
                  </div>
                </div>
                <Link to="/subscription">
                  <Button 
                    size="sm"
                    className="rounded-full px-5 bg-[#A87C6D] hover:bg-[#956d5f] text-white text-sm whitespace-nowrap"
                    data-testid="upgrade-banner-btn"
                  >
                    Join Inner Circle
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
              {/* Progress bar */}
              <div className="mt-4">
                <div className="h-1.5 bg-[#E6E2DD] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#A87C6D] transition-all duration-500"
                    style={{ width: `${(subscriptionStatus.check_ins_used / subscriptionStatus.check_in_limit) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Check-in CTA */}
        <div className="mb-8 animate-slide-up">
          <Link to="/check-in">
            <div className="paper-card rounded-3xl p-8 md:p-10 text-center hover-lift cursor-pointer transition-all duration-300 hover:shadow-lg">
              <div className="w-16 h-16 rounded-full bg-[#A87C6D]/10 flex items-center justify-center mx-auto mb-6 overflow-hidden">
                {auth.user?.profile_photo ? (
                  <img 
                    src={auth.user.profile_photo.startsWith('http') ? auth.user.profile_photo : `${API.replace('/api', '')}${auth.user.profile_photo}`}
                    alt={auth.user?.name || 'Profile'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Feather className="w-7 h-7 text-[#A87C6D]" />
                )}
              </div>
              <h2 
                className="text-2xl font-normal text-[#2D2A26] mb-3"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Begin your check-in
              </h2>
              <p className="text-[#4A4744] font-light mb-6">
                A few gentle prompts to help you feel and reflect.
              </p>
              <Button 
                className="rounded-full px-8 py-5 bg-[#A87C6D] hover:bg-[#956d5f] text-white"
                data-testid="begin-checkin-btn"
              >
                Begin
              </Button>
            </div>
          </Link>
        </div>

        {/* Saved Prompts Link */}
        <div className="mb-16 animate-slide-up">
          <Link to="/saved-prompts">
            <div className="paper-card rounded-2xl p-5 flex items-center justify-between hover-lift cursor-pointer transition-all duration-300 hover:shadow-md">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#A87C6D]/10 flex items-center justify-center">
                  <Bookmark className="w-5 h-5 text-[#A87C6D]" />
                </div>
                <div>
                  <h3 className="text-[#2D2A26] font-medium">Saved Prompts</h3>
                  <p className="text-sm text-[#6E6A66]">Prompts that resonated with you</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-[#6E6A66]" />
            </div>
          </Link>
        </div>

        {/* Weekly Emotional Summary - Inner Circle Only */}
        {weeklySummary?.has_data && subscriptionStatus?.plan !== 'free' && (
          <div className="mb-12 animate-slide-up delay-100" style={{ animationFillMode: 'forwards' }}>
            <div className="flex items-center gap-2 mb-4">
              <Crown className="w-4 h-4 text-[#A87C6D]" />
              <h3 
                className="text-xl font-normal text-[#2D2A26]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Your Week in Feelings
              </h3>
            </div>
            
            <div className="paper-card rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-3xl font-light text-[#2D2A26]">{weeklySummary.check_in_count}</p>
                  <p className="text-sm text-[#6E6A66]">check-in{weeklySummary.check_in_count !== 1 ? 's' : ''} this week</p>
                </div>
                {weeklySummary.emotional_balance && (
                  <div className="flex gap-1 h-8">
                    {weeklySummary.emotional_balance.positive > 0 && (
                      <div 
                        className="bg-[#7A9E7E] rounded-l"
                        style={{ width: `${Math.max(weeklySummary.emotional_balance.positive, 10)}px` }}
                        title={`${weeklySummary.emotional_balance.positive}% grounded`}
                      />
                    )}
                    {weeklySummary.emotional_balance.challenging > 0 && (
                      <div 
                        className="bg-[#A87C6D]"
                        style={{ width: `${Math.max(weeklySummary.emotional_balance.challenging, 10)}px` }}
                        title={`${weeklySummary.emotional_balance.challenging}% processing`}
                      />
                    )}
                    {weeklySummary.emotional_balance.neutral > 0 && (
                      <div 
                        className="bg-[#E6E2DD] rounded-r"
                        style={{ width: `${Math.max(weeklySummary.emotional_balance.neutral, 10)}px` }}
                        title={`${weeklySummary.emotional_balance.neutral}% neutral`}
                      />
                    )}
                  </div>
                )}
              </div>
              
              {weeklySummary.top_moods?.length > 0 && (
                <div>
                  <p className="text-xs text-[#6E6A66] uppercase tracking-wider mb-2">What you felt most</p>
                  <div className="flex flex-wrap gap-2">
                    {weeklySummary.top_moods.slice(0, 4).map((m, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1 rounded-full bg-[#F5F2ED] text-[#4A4744] text-sm"
                      >
                        {m.mood}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Emotional Patterns */}
        {auth.user?.emotional_patterns?.length > 0 && (
          <div className="mb-12 animate-slide-up delay-100" style={{ animationFillMode: 'forwards' }}>
            <h3 
              className="text-xl font-normal text-[#2D2A26] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              What you've been holding
            </h3>
            <div className="flex flex-wrap gap-2">
              {auth.user.emotional_patterns.slice(-5).map((pattern, idx) => (
                <span 
                  key={idx}
                  className="px-4 py-2 rounded-full bg-[#E6E2DD] text-[#4A4744] text-sm font-light"
                >
                  {pattern}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Personalized Insights - Inner Circle Only */}
        {insights?.has_access && insights?.insights?.has_enough_data && (
          <div className="mb-12 animate-slide-up delay-150" style={{ animationFillMode: 'forwards' }}>
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-4 h-4 text-[#A87C6D]" />
              <h3 
                className="text-xl font-normal text-[#2D2A26]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Over time...
              </h3>
            </div>
            <p className="text-sm text-[#6E6A66] font-light mb-4">
              The Soft Ritual may reflect patterns it notices — not to define you, but to support awareness.
            </p>
            <div className="paper-card rounded-2xl p-6 space-y-4">
              {/* Personalized Message */}
              {insights.insights.personalized_message && (
                <p className="text-[#4A4744] font-light leading-relaxed italic">
                  "{insights.insights.personalized_message}"
                </p>
              )}
              
              {/* Mood Trend */}
              {insights.insights.mood_trends?.trend_description && (
                <div className="pt-4 border-t border-[#E8E4DE]">
                  <p className="text-sm text-[#6E6A66] mb-1">Mood Trend</p>
                  <p className="text-[#2D2A26] font-light">
                    {insights.insights.mood_trends.trend_description}
                  </p>
                </div>
              )}

              {/* Day Pattern Insight */}
              {insights.insights.day_patterns?.insight && (
                <div className="pt-4 border-t border-[#E8E4DE]">
                  <p className="text-sm text-[#6E6A66] mb-1">Day Patterns</p>
                  <p className="text-[#2D2A26] font-light">
                    {insights.insights.day_patterns.insight}
                  </p>
                </div>
              )}

              {/* Theme Insight */}
              {insights.insights.theme_patterns?.insight && (
                <div className="pt-4 border-t border-[#E8E4DE]">
                  <p className="text-sm text-[#6E6A66] mb-1">Recurring Themes</p>
                  <p className="text-[#2D2A26] font-light">
                    {insights.insights.theme_patterns.insight}
                  </p>
                </div>
              )}

              {/* Streak */}
              {insights.insights.streak_info?.current_streak > 0 && (
                <div className="pt-4 border-t border-[#E8E4DE] flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#A87C6D]/10 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-[#A87C6D]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#6E6A66]">Current Streak</p>
                    <p className="text-[#2D2A26] font-medium">{insights.insights.streak_info.current_streak} days</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Insights Teaser for Free Users */}
        {insights && !insights.has_access && (
          <div className="mb-12 animate-slide-up delay-150" style={{ animationFillMode: 'forwards' }}>
            <div className="paper-card rounded-2xl p-6 bg-gradient-to-br from-[#A87C6D]/5 to-transparent border border-[#A87C6D]/20">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-4 h-4 text-[#A87C6D]" />
                <h3 className="text-lg font-normal text-[#2D2A26]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Over time...
                </h3>
              </div>
              <p className="text-[#4A4744] font-light text-sm mb-4">
                Inner Circle members receive gentle reflections on what The Soft Ritual notices — mood trends, day patterns, and recurring themes. Not to define you, but to support awareness.
              </p>
              <Link to="/subscription">
                <Button variant="outline" className="text-[#A87C6D] border-[#A87C6D] hover:bg-[#A87C6D]/10 rounded-full text-sm">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Quick Rituals */}
        <div className="mb-12 animate-slide-up delay-200" style={{ animationFillMode: 'forwards', opacity: 0 }}>
          <h3 
            className="text-xl font-normal text-[#2D2A26] mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Quick rituals
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {rituals.slice(0, 6).map((ritual) => {
              const IconComponent = RITUAL_ICONS[ritual.icon] || Heart;
              return (
                <button 
                  key={ritual.id}
                  onClick={() => setSelectedRitual(ritual)}
                  className="paper-card rounded-2xl p-4 text-center hover-lift cursor-pointer transition-all duration-200 hover:shadow-md text-left"
                  data-testid={`ritual-${ritual.id}`}
                >
                  <IconComponent className="w-6 h-6 text-[#A87C6D] mx-auto mb-2" />
                  <p className="text-sm font-medium text-[#2D2A26] text-center">{ritual.name}</p>
                  <p className="text-xs text-[#6E6A66] text-center">{ritual.duration}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Ritual Modal */}
        {selectedRitual && (
          <div 
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedRitual(null)}
          >
            <div 
              className="bg-[#FDFBF7] rounded-3xl max-w-md w-full p-8 shadow-xl animate-fade-in"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-6">
                <div className="w-12 h-12 rounded-full bg-[#A87C6D]/10 flex items-center justify-center">
                  {(() => {
                    const IconComponent = RITUAL_ICONS[selectedRitual.icon] || Heart;
                    return <IconComponent className="w-6 h-6 text-[#A87C6D]" />;
                  })()}
                </div>
                <button 
                  onClick={() => setSelectedRitual(null)}
                  className="text-[#6E6A66] hover:text-[#4A4744] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <h3 
                className="text-2xl font-normal text-[#2D2A26] mb-2"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {selectedRitual.name}
              </h3>
              <p className="text-sm text-[#A87C6D] mb-6">{selectedRitual.duration}</p>
              
              <p className="text-[#4A4744] font-light leading-relaxed mb-8">
                {selectedRitual.description}
              </p>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => setSelectedRitual(null)}
                  variant="outline"
                  className="flex-1 rounded-full border-[#E6E2DD] text-[#4A4744] hover:bg-[#F5F2ED]"
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    setSelectedRitual(null);
                    navigate('/check-in');
                  }}
                  className="flex-1 rounded-full bg-[#A87C6D] hover:bg-[#956d5f] text-white"
                >
                  Start check-in
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Soft Reflections - Memory, not archive */}
        {softReflections.length > 0 && (
          <div className="mb-8 animate-slide-up delay-300" style={{ animationFillMode: 'forwards', opacity: 0 }}>
            <h3 
              className="text-xl font-normal text-[#2D2A26] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              What we've noticed
            </h3>
            <div className="space-y-3">
              {softReflections.map((reflection, index) => (
                <div 
                  key={index}
                  className="p-4 rounded-2xl bg-[#A87C6D]/5 border border-[#A87C6D]/10"
                >
                  <p className="text-[#4A4744] font-light italic">
                    {reflection}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Sessions - Simplified */}
        {recentSessions.length > 0 && (
          <div className="animate-slide-up delay-400" style={{ animationFillMode: 'forwards', opacity: 0 }}>
            <div className="flex items-center justify-between mb-4">
              <h3 
                className="text-xl font-normal text-[#2D2A26]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Recent check-ins
              </h3>
              <Link to="/history" className="text-sm text-[#A87C6D] hover:underline">
                View all
              </Link>
            </div>
            <div className="space-y-3">
              {recentSessions.map((session) => (
                <div 
                  key={session.session_id}
                  className="paper-card rounded-2xl p-4 hover-lift cursor-pointer"
                  onClick={() => navigate('/history')}
                  data-testid={`session-${session.session_id}`}
                >
                  <p className="text-[#2D2A26] font-light line-clamp-2 mb-2">
                    {session.user_message?.substring(0, 100)}...
                  </p>
                  <div className="flex items-center gap-2 text-xs text-[#6E6A66]">
                    <Clock className="w-3 h-3" />
                    <span>{formatDate(session.created_at)}</span>
                    {session.emotional_themes?.length > 0 && (
                      <>
                        <span>•</span>
                        <span className="text-[#A87C6D]">{session.emotional_themes[0]}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
